/**
 * The pure layout decision behind the controls row's overflow: given how wide every pill measures
 * and how much room the row has, how many of them (counted from the front) fit on one line.
 *
 * Kept apart from the DOM measuring that feeds it (`useFitPills.ts`) so the decision itself — the
 * part with a right and wrong answer — can be tested without rendering anything. The row never
 * wraps: whatever does not fit moves into a "…" menu instead, so this only ever answers "how many",
 * never "which ones don't fit" (the caller already knows that: everything after the count).
 */

export interface FitPillsResult {
  /** How many pills, counted from index 0, stay on the line. Always at least 1. */
  visibleCount: number;
  /** Whether a "…" trigger is needed at all — false when everything already fit. */
  overflowed: boolean;
}

/**
 * `moreWidth` is the "…" pill's own width plus the gap in front of it, measured and passed in by the
 * caller before this runs — reserved on every step, not just checked once, so the last visible pill
 * never ends up sitting half under the menu it made necessary. `budget` is the space available to the
 * pills track itself, not the whole row: the loop switcher and the List/Details picker are fixed
 * width and are not this function's concern.
 */
export const fitPills = (
  pillWidths: readonly number[],
  budget: number,
  gap: number,
  moreWidth: number,
): FitPillsResult => {
  if (pillWidths.length === 0) return { visibleCount: 0, overflowed: false };

  const total = pillWidths.reduce((sum, width) => sum + width, 0) + gap * (pillWidths.length - 1);
  if (total <= budget) return { visibleCount: pillWidths.length, overflowed: false };

  let used = 0;
  let shown = 0;
  for (const width of pillWidths) {
    const addition = width + (shown > 0 ? gap : 0);
    if (used + addition + moreWidth > budget) break;
    used += addition;
    shown += 1;
  }

  return { visibleCount: Math.max(shown, 1), overflowed: true };
};
